import { useEffect } from "react";

import { useQueries } from "@tanstack/react-query";

import type {
    ConversionRatesCategoriesOverviewListData,
    ConversionRatesCategoriesOverviewListParams,
    ConversionRatesCategoryOverviewListData,
} from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import { getQueryOptions, useApiQuery } from "app/modules/api-client";
import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";

type SharedParams = Omit<ConversionRatesCategoriesOverviewListParams, "category">;

/**
 * The endpoint returns an empty body when a category has no conversion data, so we normalize
 * incomplete payloads to undefined, mirroring the single-category hook.
 */
const normalizeCategoryData = (
    data: Partial<ConversionRatesCategoryOverviewListData> | undefined,
): ConversionRatesCategoryOverviewListData | undefined =>
    data && data.totalConvertedVisits && data.avgConversionRate
        ? (data as ConversionRatesCategoryOverviewListData)
        : undefined;

/**
 * The combined "categories overview" endpoint doesn't support segment-scoped custom industries, so when
 * any compared category has segments we fall back to N parallel single-category requests (the same
 * endpoint single mode already uses successfully for segment CIs) and merge them into the shape the
 * combined endpoint would have returned. Categories without data are simply omitted, the same way the
 * combined endpoint omits them, so the table still renders for whichever categories do have data.
 */
const buildFallbackEntries = (
    categories: ICategoryComparison[],
    results: Array<{ data?: ConversionRatesCategoryOverviewListData }>,
): ConversionRatesCategoriesOverviewListData => {
    const normalizedResults = results.map((result) => normalizeCategoryData(result.data));
    const maxConvertedVisits = Math.max(0, ...normalizedResults.map((data) => data?.totalConvertedVisits.value ?? 0));

    return categories.reduce<ConversionRatesCategoriesOverviewListData>((entries, { category }, index) => {
        const data = normalizedResults[index];
        if (!data) {
            return entries;
        }

        entries.push({
            category: category.forApi,
            convertedVisits: data.totalConvertedVisits.value,
            convertedVisitsChange: data.totalConvertedVisits.change ?? null,
            avgConversionRate: data.avgConversionRate.value ?? null,
            normalizedConvertedVisits:
                maxConvertedVisits > 0 ? data.totalConvertedVisits.value / maxConvertedVisits : 0,
        });
        return entries;
    }, []);
};

export const useConversionRateComparisonOverviewData = (
    categories: ICategoryComparison[],
    sharedParams: SharedParams,
    { enabled, hasSelectedCategoryWithSegments }: { enabled: boolean; hasSelectedCategoryWithSegments: boolean },
) => {
    const combinedParams = {
        ...sharedParams,
        category: categories.map(({ category }) => category.forApi),
    } satisfies ConversionRatesCategoriesOverviewListParams;

    const combinedQuery = useApiQuery(
        {
            tag: "WebConversionRates",
            methodName: "conversionRatesCategoriesOverviewList",
            options: { enabled: enabled && !hasSelectedCategoryWithSegments, placeholderData: undefined },
        },
        combinedParams,
    );

    const fallbackQueries = useQueries({
        queries: categories.map(({ category }) =>
            getQueryOptions(
                {
                    tag: "WebConversionRates",
                    methodName: "conversionRatesCategoryOverviewList",
                    options: { enabled: enabled && hasSelectedCategoryWithSegments, placeholderData: undefined },
                },
                { ...sharedParams, category: category.forApi },
            ),
        ),
        combine: (results) => ({
            data: buildFallbackEntries(categories, results),
            isLoading: results.some((result) => result.isLoading),
            isError: results.some((result) => result.isError),
            error: results.find((result) => result.error)?.error ?? null,
        }),
    });

    const { data, isLoading, isError, error } = hasSelectedCategoryWithSegments
        ? fallbackQueries
        : {
              data: combinedQuery.data,
              isLoading: combinedQuery.isLoading,
              isError: combinedQuery.isError,
              error: combinedQuery.error,
          };

    useEffect(() => {
        if (isError) {
            Sentry.errorTracker({
                e: error instanceof Error ? error : new Error("Conversion rate categories overview request failed"),
                message: "Failed to load conversion rate categories overview",
                level: "error",
            });
        }
    }, [isError, error]);

    return { data, isLoading, isError };
};
